import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Linking, Platform, View } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { useAuthStore, useCreditPacksStore, useModalStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import { creditPacksApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import Button from '@/components/Buttons/Button';
import AutoTopupEditor, { money, topupStatus } from '@/components/Credits/AutoTopupEditor';

export default function AutoTopUpSettings({ startEditing = false, onClose }) {
  const { colors } = useTheme();
  const user = useAuthStore(s => s.user);
  const update = useAuthStore(s => s.updateAutoTopupSettings);
  const packs = useCreditPacksStore(s => s.creditPacks);
  const fetchPacks = useCreditPacksStore(s => s.fetchCreditPacks);
  const { openModal } = useModalStore(s => s.actions);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(startEditing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const load = useCallback(async () => {
    setError('');
    try {
      const response = await creditPacksApi.getAutoTopupSettings();
      setSettings(response.autoTopup); update(response.autoTopup);
      await fetchPacks();
    } catch (_) { setError('Settings could not load. Please try again.'); }
  }, [fetchPacks, update]);
  useEffect(() => { if (Platform.OS === 'web' || Platform.OS === 'macos') load(); }, [load]);
  if (Platform.OS !== 'web' && Platform.OS !== 'macos') return null;
  const save = async body => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const response = await creditPacksApi.updateAutoTopupSettings(body);
      if (!response.autoTopup) throw new Error('Reload to check whether your settings saved.');
      setSettings(s => ({ ...s, ...response.autoTopup })); update(response.autoTopup);
      setEditing(false); setNotice(body.enabled ? 'Auto top-up settings saved.' : 'Auto top-up is off. A payment already in progress may still complete.');
      if (body.enabled) onClose?.();
    } catch (err) { setError(err?.message || 'Settings could not save. Reload to check their current state.'); }
    finally { setBusy(false); }
  };
  const setup = async () => {
    setBusy(true); setError('');
    try {
      const response = await creditPacksApi.startWhopAutoTopupSetup();
      if (Platform.OS === 'macos') await Linking.openURL(response.checkoutUrl);
      else await WebBrowser.openBrowserAsync(response.checkoutUrl);
      setNotice('After saving your payment method in Whop, reload here to review and authorize auto top-up. No auto top-up is enabled by saving a card.');
    } catch (err) { setError(err?.message || 'Payment-method setup could not open.'); }
    finally { setBusy(false); }
  };
  const whop = settings?.provider === 'whop';
  const available = !whop || settings?.whopAvailable;
  const pending = ['payment_pending', 'fulfillment_pending'].includes(settings?.status);
  const card = whop ? 'Saved Whop payment method' : user?.valmar?.maskedCardData?.maskedNumber
    ? `${user.valmar.maskedCardData.cardType || 'Card'} •••• ${user.valmar.maskedCardData.maskedNumber.slice(-4)}` : 'Saved card';
  return <View style={{ gap: 18, paddingVertical: 18 }}>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
      <Text size="l" weight="semibold">Auto top-up</Text>
      {settings && <View style={{ backgroundColor: colors.solid_color_secondary, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8 }}><Text size="xs">{topupStatus[settings.status] || 'Review required'}</Text></View>}
    </View>
    {!settings && !error && <ActivityIndicator />}
    {!!notice && <View accessibilityRole="status"><Text size="s" color="secondary">{notice}</Text></View>}
    {!!error && <View accessibilityRole="alert"><Text size="s" color="danger">{error}</Text></View>}
    {settings && <>
      {pending && <Text size="s" color="secondary">We’re checking an existing payment. Another automatic charge is blocked until it is resolved. You can still turn auto top-up off.</Text>}
      {!available && <Text size="s" color="secondary">Whop auto top-up is not available yet. One-time credit purchases remain available.</Text>}
      {!settings.hasPaymentMethod && available && <Text size="s" color="secondary">Save a payment method to set up automatic credit purchases.</Text>}
      {editing && available && settings.hasPaymentMethod && !pending && packs.length > 0 ? <AutoTopupEditor key={`${settings.version}:${settings.paymentMethodFingerprint}`} settings={settings} packs={packs} cardLabel={card} busy={busy} onSave={save} onCancel={() => { setEditing(false); setError(''); onClose?.(); }} /> : <>
        {settings.authorization && <View style={{ gap: 8 }}>
          <Text size="s">Buy {settings.authorization.credits.toLocaleString()} credits for {money(settings.authorization.priceCents / 100)} when my balance falls below {settings.thresholdCredits.toLocaleString()}.</Text>
          <Text size="s" color="secondary">{settings.monthlySpendLimit === null ? 'No monthly spending limit' : `${money(settings.monthlySpendLimit)} monthly limit`} · {money(settings.currentMonthSpend)} spent or reserved this month</Text>
        </View>}
        {!settings.authorization && available && <Text size="s" color="secondary">Choose when to refill, how many credits to buy, and how much to spend each month.</Text>}
        <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
          {available && settings.hasPaymentMethod && !pending && <Button text={settings.enabled ? 'Manage auto top-up' : 'Set up auto top-up'} onPress={() => setEditing(true)} isDisabled={busy} />}
          {!settings.hasPaymentMethod && whop && settings.whopSetupAvailable && <Button text="Save payment method with Whop" onPress={setup} isDisabled={busy} />}
          {!settings.hasPaymentMethod && !whop && <Button text="Buy credits to save a card" onPress={() => openModal('CreditsPurchaseModal')} />}
          {settings.enabled && <Button text="Turn off auto top-up" onPress={() => save({ enabled: false })} isDisabled={busy} />}
        </View>
        {settings.enabled && <Text size="xs" color="secondary">At most once every 24 hours. Checks can take 10 minutes. Work may still pause. Turning this off does not cancel a payment already in progress.</Text>}
      </>}
    </>}
    <View style={{ flexDirection: 'row', gap: 10 }}><Button text="Reload settings" onPress={load} isDisabled={busy} />{onClose && !editing && <Button text="Done" onPress={onClose} />}</View>
  </View>;
}
